import React, {} from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../../../components/Button';
import Input from '../../../../components/Input';
import PhoneInput from '../../../../components/PhoneInput';
import { PropertyContactDetail } from '../../../../interfaces/property.interface';
import { VALIDATION_FIELD_MAPPING } from '../../../../utils/appConstant';

interface Props {
  data: PropertyContactDetail[];
  onChange: (data: PropertyContactDetail[]) => void;
  view?: boolean;
  errors?: any;
}

const PropertyContactDetailsTable: React.FC<Props> = ({ data, onChange, view, errors }) => {
  const { t } = useTranslation();

  const handleAddRow = () => {
    const newRow: PropertyContactDetail = {
      contact_person: '',
      contact_number: '',
      email: '',
      is_primary: false,
    };
    onChange([...data, newRow]);
  };

  const handleRemoveRow = (index: number) => {
    const updatedData = [...data];
    updatedData.splice(index, 1);
    onChange(updatedData);
  };

  const handleInputChange = (index: number, field: keyof PropertyContactDetail, value: any) => {
    // strict 10 digit enforcement logic is now inside PhoneInputComponent
    // But we still need to support direct value from PhoneInput which passes the string directly via event

    const updatedData = [...data];
    updatedData[index] = { ...updatedData[index], [field]: value };
    // Handle primary logic: if set to true, unset others (if singular primary is required)
    if (field === 'is_primary' && value === true) {
        updatedData.forEach((row, i) => {
            if (i !== index) row.is_primary = false;
        });
    }
    onChange(updatedData);
  };

  return (
    <div className="table-responsive text-nowrap mt-3">
      <table className="table table-bordered">
        <thead>
          <tr>
            <th style={{ minWidth: '200px' }}>{t('properties.addEdit.pageTexts.form.contactDetails.contactPerson')}</th>
            <th style={{ minWidth: '250px' }}>{t('properties.addEdit.pageTexts.form.contactDetails.contactNumber')}</th>
            <th style={{ minWidth: '200px' }}>{t('properties.addEdit.pageTexts.form.contactDetails.email')}</th>
            <th>{t('properties.addEdit.pageTexts.form.contactDetails.isPrimary')}</th>
            {!view && <th>{t('common.buttons.actions')}</th>}
          </tr>
        </thead>
        <tbody>
          {data.map((row, index) => (
            <tr key={index}>
              <td>
                <Input
                  name={`${VALIDATION_FIELD_MAPPING.contact_person}-${index}`}
                  value={row.contact_person}
                  onChange={(e) => handleInputChange(index, 'contact_person', e.target.value)}
                  disabled={view}
                   placeholder={t('properties.addEdit.pageTexts.form.contactDetails.contactPerson')}
                   error={!!errors?.[`contact_person-${index}`]}
                   errorMessage={errors?.[`contact_person-${index}`]}
                />
              </td>
              <td>
                <PhoneInput
                  name={`${VALIDATION_FIELD_MAPPING.contact_number}-${index}`}
                  id={`${VALIDATION_FIELD_MAPPING.contact_number}-${index}`}
                  type="text"
                  value={'+91' + (row.contact_number || '')}
                  onChange={(e) => {
                    const val = e.target.value.replace(/^\+91/, '');
                    handleInputChange(index, 'contact_number', val);
                  }}
                  disabled={view}
                  placeholder={t('properties.addEdit.pageTexts.form.contactDetails.contactNumber')}
                  error={!!errors?.[`contact_number-${index}`]}
                  errorMessage={errors?.[`contact_number-${index}`]}
                />
              </td>
              <td>
                <Input
                  name={`${VALIDATION_FIELD_MAPPING.email}-${index}`}
                  value={row.email}
                  onChange={(e) => handleInputChange(index, 'email', e.target.value)}
                  disabled={view}
                  placeholder={t('properties.addEdit.pageTexts.form.contactDetails.email')}
                  type="email"
                  error={!!errors?.[`email-${index}`]}
                  errorMessage={errors?.[`email-${index}`]}
                />
              </td>
              <td className="text-center align-middle">
                <input
                  type="checkbox"
                  className="form-check-input mt-0"
                  checked={row.is_primary}
                  onChange={(e) => handleInputChange(index, 'is_primary', e.target.checked)}
                  disabled={view}
                />
              </td>
              {!view && (
                <td className="text-center">
                  <Button
                    className="btn btn-sm btn-icon btn-text-danger waves-effect waves-light rounded-pill"
                    onClick={() => handleRemoveRow(index)}
                    icon={<i className="ri-delete-bin-line"></i>}
                  />
                </td>
              )}
            </tr>
          ))}
          {!view && (
            <tr>
              <td colSpan={5}>
                <Button
                  className="btn btn-sm btn-primary"
                  onClick={handleAddRow}
                  text={t('common.buttons.add')}
                  icon={<i className="ri-add-line me-1"></i>}
                />
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default PropertyContactDetailsTable;
